import React, { useEffect, useState, useRef } from "react";
import { message } from "antd";
import AdminLayout from "./components/AdminLayout";
import axios from "axios";
import "./AdminGallery.css";

const AdminProductCategory = () => {
  const [category, setCategory] = useState("");
  const [image, setImage] = useState(null);
  const [allCategory, setAllCategory] = useState([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const normalizeCategoryImgUrl = (imgValue) => {
    const v = String(imgValue || "").trim();
    if (!v) return "";
    if (v.startsWith("http://") || v.startsWith("https://")) return v;

    // new DB style: "/categoryImages/xxx.png"
    if (v.startsWith("/")) return v;

    // old DB style: "xxx.png"
    return `/categoryImages/${v.replace(/^categoryImages[\\/]/, "")}`;
  };

  const addProductCategory = async (e) => {
    if (e?.preventDefault) e.preventDefault();

    try {
      if (!category.trim()) {
        return message.error("Enter category");
      }

      const formData = new FormData();
      formData.append("category", category.trim());
      if (image) formData.append("image", image);

      const res = await axios.post("/api/admin/add-product-category", formData, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (res.data?.success) {
        message.success(res.data.message || "Category added");
        setCategory("");
        setImage(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        getAllCategory();
      } else {
        message.error(res.data?.message || "Failed to add category");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  };

  const getAllCategory = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/get-product-category", {
        headers: getAuthHeaders(),
      });

      if (res.data?.success) {
        setAllCategory(Array.isArray(res.data.data) ? res.data.data : []);
      } else {
        message.error(res.data?.message || "Failed to fetch categories");
        setAllCategory([]);
      }
    } catch (error) {
      console.log(error);
      message.error("Failed to fetch categories");
      setAllCategory([]);
    } finally {
      setLoading(false);
    }
  };

  async function handleDelete(id) {
    const confirmDelete = window.confirm("Are you sure to delete?");
    if (!confirmDelete) return;

    try {
      const res = await axios.post(
        "/api/admin/delete-product-category",
        { id },
        { headers: getAuthHeaders() }
      );

      if (res.data?.success) {
        message.success(res.data.message || "Deleted");
        setAllCategory((prev) => prev.filter((item) => item._id !== id));
      } else {
        message.error(res.data?.message || "Delete failed");
      }
    } catch (error) {
      console.log(error);
      message.error("Something went wrong");
    }
  }

  useEffect(() => {
    getAllCategory();
  }, []);

  return (
    <AdminLayout>
      <div className="admin-users-container">
        <div className="page-title">
          <h3 className="m-0">Category</h3>
        </div>
        <hr />

        <div className="gallery-container">
          {/* Form */}
          <form className="gallery-form" onSubmit={addProductCategory}>
            <input
              type="text"
              name="category"
              placeholder="Enter category"
              className="form-control"
              onChange={(e) => setCategory(e.target.value)}
              value={category}
            />

            <input
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              className="form-control"
              ref={fileInputRef}
              onChange={(e) =>
                setImage(e.target.files?.[0] ? e.target.files[0] : null)
              }
            />

            <button type="submit" className="gallery-btn">
              Add Category
            </button>
          </form>

          {/* toolbar */}
          <div className="gallery-toolbar">
            <div className="gallery-count">
              {loading ? "Loading..." : `Total: ${allCategory.length}`}
            </div>
            <button
              type="button"
              className="gallery-btn outline"
              onClick={getAllCategory}
              disabled={loading}
            >
              Refresh
            </button>
          </div>

          {/* table */}
          <div className="table-wrap">
            <table className="table table-bordered admin-gallery-table">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Srno</th>
                  <th style={{ width: 120 }}>Image</th>
                  <th>Category</th>
                  <th style={{ width: 90 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!loading && allCategory.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="empty-row">
                      No categories found
                    </td>
                  </tr>
                ) : (
                  allCategory.map((item, index) => {
                    const url = normalizeCategoryImgUrl(item?.image);

                    return (
                      <tr key={item._id}>
                        <td>{index + 1}</td>
                        <td>
                          {url ? (
                            <img
                              className="gallery-thumb"
                              src={url}
                              alt={item?.name || "category"}
                              loading="lazy"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="name-cell">{item?.name || "-"}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDelete(item._id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* mobile cards */}
          <div className="gallery-cards">
            {allCategory.map((item) => {
              const url = normalizeCategoryImgUrl(item?.image);
              return (
                <div className="gallery-card" key={item._id}>
                  <div className="card-left">
                    {url ? (
                      <img
                        className="gallery-card-img"
                        src={url}
                        alt={item?.name || "category"}
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    ) : (
                      <div className="gallery-card-img placeholder">
                        No Image
                      </div>
                    )}
                  </div>

                  <div className="card-mid">
                    <div className="card-title">{item?.name || "-"}</div>
                    <div className="card-sub">{url || "-"}</div>
                  </div>

                  <div className="card-right">
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDelete(item._id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProductCategory;
